import { BasicSelect, LocalizedValue } from '@agro-club/frontend-shared'
import useLangPicker from 'hooks/useLangPicker'
import { TargetSkuTypes } from 'modules/domain/target2sku/common/types'
import { endpoints } from 'modules/endpoints'
import React, { useMemo } from 'react'
import { EntitySelect, EntitySelectProps } from 'views/components/EntitySelect/EntitySelect'
import { createGetList } from './common'

export type TargetSkuCategory = {
  title: LocalizedValue
  id: string
}

type TargetSkuCategoryFilter = {
  manufacturer_id?: string
  territory_id?: string
  only_current_season?: boolean
  title?: string
}

type TargetSkuCategorySelectProps = EntitySelectProps<TargetSkuCategory> & {
  manufacturerId?: string
  territoryId?: string
  onlyCurrentSeason?: boolean
  valueLabel?: string
  scope?: TargetSkuTypes | 'default' | 'pedigreed'
}

const getList = createGetList<TargetSkuCategory, TargetSkuCategoryFilter>(endpoints.target2sku('company-subcategories'))

const getListTerritoryFilter = createGetList<TargetSkuCategory, TargetSkuCategoryFilter>(
  endpoints.target2sku('territory/subcategories'),
)

const getListRetailerFilter = createGetList<TargetSkuCategory, TargetSkuCategoryFilter>(
  endpoints.target2sku('retailer/subcategories'),
)

const getListFarmerFilter = createGetList<TargetSkuCategory, TargetSkuCategoryFilter>(
  endpoints.target2sku('farmer/subcategories'),
)

const getListCropFilter = createGetList<TargetSkuCategory, TargetSkuCategoryFilter>(
  endpoints.target2sku('territory-crop/subcategories'),
)

const getListPedigreedFilter = createGetList<TargetSkuCategory, TargetSkuCategoryFilter>(
  endpoints.target2sku('pedigreed-territory/subcategories'),
)

const getListPedigreedGrowerFilter = createGetList<TargetSkuCategory, TargetSkuCategoryFilter>(
  endpoints.target2sku('pedigreed-grower/subcategories'),
)

const getPedigreedFormList = createGetList<TargetSkuCategory, TargetSkuCategoryFilter>(
  endpoints.target2sku('pedigreed-subcategories'),
)

const scopeManagerMap = {
  default: getList,
  pedigreed: getPedigreedFormList,
  [TargetSkuTypes.Farmer]: getListFarmerFilter,
  [TargetSkuTypes.Retailer]: getListRetailerFilter,
  [TargetSkuTypes.Territory]: getListTerritoryFilter,
  [TargetSkuTypes.Crop]: getListCropFilter,
  [TargetSkuTypes.Pedigreed]: getListPedigreedFilter,
  [TargetSkuTypes.PedigreedGrower]: getListPedigreedGrowerFilter,
}

const getValue = (item: TargetSkuCategory) => item.id

export const TargetSkuSubcategorySelect: React.VFC<TargetSkuCategorySelectProps> = ({
  manufacturerId,
  territoryId,
  onlyCurrentSeason,
  isDisabled,
  valueLabel,
  onChange,
  scope = 'default',
  ...props
}) => {
  const { pick } = useLangPicker()
  const filter = useMemo(
    () => ({
      manufacturer_id: manufacturerId,
      territory_id: territoryId,
      only_current_season: onlyCurrentSeason || undefined,
    }),
    [manufacturerId, territoryId, onlyCurrentSeason],
  )
  const fakeValue = useMemo(() => (valueLabel ? { title: { en: valueLabel }, id: 'fake' } : undefined), [valueLabel])

  const getLabel = (item: TargetSkuCategory) => pick(item.title)

  const testId = 'target-subcategory-select'

  return isDisabled ? (
    <BasicSelect
      isDisabled
      getOptionLabel={getLabel}
      getOptionValue={getValue}
      {...props}
      value={fakeValue}
      data-test-id={testId}
    />
  ) : (
    <EntitySelect
      filter={filter}
      getEntityList={scopeManagerMap[scope]}
      getOptionLabel={getLabel}
      getOptionValue={getValue}
      onChange={onChange}
      searchParamKey="title"
      data-test-id={testId}
      {...props}
    />
  )
}
